// Phase 5.1 — Customers list table.
//
// Replaces the MUI <Table> + antd Dropdown/Menu action in customer.tsx with the
// shared DataTable and a DropdownMenu. Presentation only: search + server
// pagination + the Sort select reuse the screen's handlers; the "View" purchase-
// history link and the row Edit action call the screen's `onViewHistory(item)` /
// `onEdit(item)` (same dispatch). Sort options mirror the legacy ApparelSubmenu
// customerFilterData (Latest/Oldest/Name A-Z/Name Z-A).
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { MoreVertical, Star, TrendingDown, TrendingUp } from "lucide-react";
import moment from "moment";
import { DataTable } from "../../shared/data-table";
import { Button } from "ui/button";
import { Badge } from "ui/badge";
import { StatusBadge } from "../../shared/status-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import AppConstant from "../../Globals/AppConstants";

const SORT_OPTIONS = [
  { id: "8", name: "Outstanding (High → Low)" },
  { id: "0", name: "Latest" },
  { id: "3", name: "Oldest" },
  { id: "1", name: "Name (A-Z)" },
  { id: "2", name: "Name (Z-A)" },
  { id: "5", name: "Purchase (High → Low)" },
  { id: "6", name: "Purchase (Low → High)" },
  { id: "7", name: "Nil (Mobile Number)" },
];

// Radix <Select> forbids empty-string item values, so the "All" option uses an
// internal ALL sentinel mapped back to "" on dispatch (same convention as
// InventoryToolbar's status filter).
const WHATSAPP_ALL = "__all__";
const WHATSAPP_FILTER_OPTIONS = [
  { id: WHATSAPP_ALL, name: "All (WhatsApp)" },
  { id: "1", name: "Joined" },
  { id: "0", name: "Not Joined" },
  { id: "not_whatsapp", name: "Not a WhatsApp Number" },
];

// Mirrors AddCustomerSheet.tsx's three-way radio.
function whatsappStatusLabel(item: any): { text: string; variant: "success" | "warning" | "destructive" } {
  if (item?.isNotWhatsappNumber === 1) return { text: "Not a WhatsApp Number", variant: "destructive" };
  if (item?.whatsappGroupStatus === 1) return { text: "WhatsApp Group Joined", variant: "success" };
  return { text: "Not Joined", variant: "warning" };
}

// Date of Birth and Wedding Anniversary only ever store month/day (the year
// is a meaningless placeholder) — never show it.
const formatMonthDay = (v: any) => (v ? moment(v).format("DD-MMM") : "-");

// Indian financial year (Apr 1 - Mar 31) — same rule the backend uses to
// compute currentYear*/previousYear* per customer, so the header labels line
// up with the data.
function currentFinancialYearStart(): number {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
}
const CURRENT_FY_START = currentFinancialYearStart();
const CURRENT_FY_LABEL = `FY ${CURRENT_FY_START}–${CURRENT_FY_START + 1}`;
const PREVIOUS_FY_LABEL = `FY ${CURRENT_FY_START - 1}–${CURRENT_FY_START}`;

// Customer Credit amounts are always whole rupees, never decimals.
const formatRupees = (value: any) => Math.round(Number(value ?? 0)).toLocaleString("en-IN");

function PurchaseCell({ amount, count }: { amount: any; count: any }) {
  return (
    <div>
      <div className="tabular-nums text-foreground">₹{Number(amount ?? 0).toLocaleString("en-IN")}</div>
      <div className="text-[11px] text-muted-foreground">({Number(count ?? 0)} Purchases)</div>
    </div>
  );
}

function GrowthCell({ current, previous, currentCount, previousCount }: { current: any; previous: any; currentCount: any; previousCount: any }) {
  const currentAmount = Number(current ?? 0);
  const previousAmount = Number(previous ?? 0);

  let percent = 0;
  if (previousAmount === 0 && currentAmount > 0) percent = 100;
  else if (previousAmount === 0 && currentAmount === 0) percent = 0;
  else percent = ((currentAmount - previousAmount) / previousAmount) * 100;

  const rounded = Math.round(percent);
  const variant = rounded > 0 ? "success" : rounded < 0 ? "destructive" : "secondary";
  const Icon = rounded > 0 ? TrendingUp : TrendingDown;

  return (
    <div className="space-y-1">
      <Badge variant={variant} className="gap-1">
        {rounded !== 0 && <Icon className="h-3 w-3" />}
        {rounded > 0 ? "+" : ""}{rounded}%
      </Badge>
      {/* <div className="text-xs text-muted-foreground">
        {Number(currentCount ?? 0)} vs {Number(previousCount ?? 0)}
      </div> */}
    </div>
  );
}

export interface CustomerTableProps {
  data: any[];
  loading: boolean;
  onViewHistory: (item: any) => void;
  onEdit: (item: any) => void;
  onMerge?: (item: any) => void;
  whatsappFilter?: any;
  onWhatsappFilterChange?: (value: string) => void;
  onViewCredit?: (item: any) => void;
  onViewFeedback?: (item: any) => void;
  searchValue?: string;
  onSearch: (query: string) => void;
  searchPlaceholder?: string;
  sortBy?: any;
  onSortChange: (sortBy: string) => void;
  title?: React.ReactNode;
  headerActions?: React.ReactNode;
  pagination: { page: number; total: number; onChange: (page: number) => void };
}

export function CustomerTable({
  data,
  loading,
  onViewHistory,
  onEdit,
  onMerge,
  whatsappFilter,
  onWhatsappFilterChange,
  onViewCredit,
  onViewFeedback,
  searchValue,
  onSearch,
  searchPlaceholder,
  sortBy,
  onSortChange,
  title,
  headerActions,
  pagination,
}: CustomerTableProps) {
  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "customerCode",
        header: "ID",
        cell: ({ row }) => <span className="whitespace-nowrap font-medium">{row.original?.customerCode || "-"}</span>,
        meta: { sticky: "left" },
      },
      { id: "customerName", header: "Customer Name", cell: ({ row }) => row.original?.customerName, meta: { sticky: "left" } },
      { id: "mobileNumberList", header: "Mobile Number", cell: ({ row }) => row.original?.mobileNumberList },
      {
        id: "currentYear",
        header: `Current Year`,
        cell: ({ row }) => <PurchaseCell amount={row.original?.currentYearAmount} count={row.original?.currentYearCount} />,
      },
      {
        id: "previousYear",
        header: `Previous Year`,
        cell: ({ row }) => <PurchaseCell amount={row.original?.previousYearAmount} count={row.original?.previousYearCount} />,
      },
      {
        id: "purchaseGrowth",
        header: "Purchase Growth",
        cell: ({ row }) => (
          <GrowthCell
            current={row.original?.currentYearAmount}
            previous={row.original?.previousYearAmount}
            currentCount={row.original?.currentYearCount}
            previousCount={row.original?.previousYearCount}
          />
        ),
      },
      {
        id: "history",
        header: "Purchase History",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => onViewHistory(row.original)}
            className="text-primary hover:underline"
          >
            {AppConstant?.view}
          </button>
        ),
      },
      {
        id: "dateOfBirth",
        header: "Date of Birth",
        cell: ({ row }) => <span className="whitespace-nowrap">{formatMonthDay(row.original?.dateOfBirth)}</span>,
      },
      {
        id: "weddingAnniversary",
        header: "Wedding Anniversary",
        cell: ({ row }) => <span className="whitespace-nowrap">{formatMonthDay(row.original?.weddingAnniversary)}</span>,
      },
      {
        id: "whatsappGroupStatus",
        header: "WhatsApp Group Joined",
        cell: ({ row }) => {
          const { text, variant } = whatsappStatusLabel(row.original);
          return <StatusBadge status={text} variant={variant} />;
        },
      },
      {
        id: "feedbackRating",
        header: "Feedback Rating",
        cell: ({ row }) => {
          const rating = row.original?.feedbackRating;
          if (rating == null) return <span className="text-muted-foreground">—</span>;
          if (!onViewFeedback) {
            return (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                {Number(rating)}/5
              </span>
            );
          }
          return (
            <button
              type="button"
              onClick={() => onViewFeedback(row.original)}
              className="inline-flex items-center gap-1 tabular-nums text-primary hover:underline"
            >
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              {Number(rating)}/5
            </button>
          );
        },
      },
      ...(onViewCredit ? [
        {
          id: "creditLimit",
          header: "Credit Limit",
          cell: ({ row }: any) => <span className="tabular-nums">₹{formatRupees(row.original?.creditLimit)}</span>,
        },
        {
          id: "creditBalance",
          header: "Outstanding",
          cell: ({ row }: any) => <span className="tabular-nums">₹{formatRupees(row.original?.creditBalance)}</span>,
        },
        {
          id: "creditHistory",
          header: "Credit History",
          cell: ({ row }: any) => {
            const item = row.original;
            if (!item?.creditLimit || Number(item.creditLimit) <= 0) return <span className="text-muted-foreground">—</span>;
            return (
              <button type="button" onClick={() => onViewCredit(item)} className="text-primary hover:underline">
                {AppConstant?.view}
              </button>
            );
          },
        },
      ] as ColumnDef<any, any>[] : []),
      {
        id: "createdOn",
        header: "Created On",
        cell: ({ row }) => moment(row.original?.createdOn).format("DD-MMM-YYYY / hh:mm A"),
      },
      {
        id: "action",
        header: "Action",
        meta: { sticky: "right" },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Row actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(item)}>{AppConstant?.edit}</DropdownMenuItem>
                {onMerge && <DropdownMenuItem onClick={() => onMerge(item)}>Merge</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    []
  );

  const sortToolbar = (
    <div className="flex items-center gap-2">
      {onWhatsappFilterChange && (
        <Select
          value={whatsappFilter != null && whatsappFilter !== "" ? String(whatsappFilter) : WHATSAPP_ALL}
          onValueChange={(v) => onWhatsappFilterChange(v === WHATSAPP_ALL ? "" : v)}
        >
          <SelectTrigger className="w-[210px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {WHATSAPP_FILTER_OPTIONS.map((o) => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <span className="text-sm text-muted-foreground">Sort</span>
      <Select value={sortBy != null ? String(sortBy) : "0"} onValueChange={(v) => onSortChange(v)}>
        <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SORT_OPTIONS.map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <DataTable
      columns={columns}
      data={data || []}
      loading={loading}
      searchable
      searchValue={searchValue}
      onSearch={onSearch}
      searchPlaceholder={searchPlaceholder}
      toolbar={sortToolbar}
      title={title}
      headerActions={headerActions}
      pagination={pagination}
    />
  );
}

export default CustomerTable;
